import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useAppContext, ACTION_TYPES } from '../Context/AppContext';
import { requestPasswordReset } from '../Shared/publicApi';
import AuthPageLayout, { AuthHeaderLink } from '../Components/AuthPageLayout';

const ForgotPassword = () => {
	const { dispatch } = useAppContext();
	const [email, setEmail] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Forgot password' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');
		setLoading(true);

		try {
			await requestPasswordReset(email.trim());
			setSubmitted(true);
		} catch (err) {
			const message =
				err?.response?.data?.email?.[0] ||
				err?.response?.data?.detail ||
				err?.message ||
				'Something went wrong. Please try again.';
			setError(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout
			headerRight={<AuthHeaderLink to="/login">Sign in</AuthHeaderLink>}
		>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
					Forgot your password?
				</Typography>
				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ lineHeight: 1.75 }}
				>
					Enter your email and we&apos;ll send you a link to reset your
					password.
				</Typography>
			</Box>

			{submitted ? (
				<Alert severity="success" sx={{ mb: 2 }}>
					Check your email for a reset link.
				</Alert>
			) : (
				<>
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					<Paper
						elevation={0}
						sx={{
							p: { xs: 2.5, sm: 3 },
							borderRadius: 3,
							border: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Box component="form" onSubmit={handleSubmit}>
							<Stack spacing={2}>
								<TextField
									label="Email"
									name="email"
									type="email"
									value={email}
									onChange={(event) => {
										setEmail(event.target.value);
										setError('');
									}}
									required
									fullWidth
									autoComplete="email"
									autoFocus
									placeholder="you@company.com"
								/>

								<Button
									type="submit"
									variant="contained"
									color="warning"
									size="large"
									disabled={loading}
									fullWidth
									sx={{ py: 1.5 }}
								>
									{loading ? 'Sending...' : 'Send reset link'}
								</Button>
							</Stack>
						</Box>
					</Paper>
				</>
			)}

			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ textAlign: 'center', mt: 3 }}
			>
				Remember your password?{' '}
				<Typography
					component={Link}
					to="/login"
					variant="body2"
					sx={{
						color: 'primary.main',
						fontWeight: 600,
						textDecoration: 'none',
						'&:hover': { textDecoration: 'underline' },
					}}
				>
					Sign in
				</Typography>
			</Typography>
		</AuthPageLayout>
	);
};

export default ForgotPassword;
